import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import TimesheetEntry from './components/TimesheetEntry';
import TimesheetSummary from './components/TimesheetSummary';
import TimesheetFilters from './components/TimesheetFilters';
import TimesheetEntryForm from './components/TimesheetEntryForm';
import ProjectManager from './components/ProjectManager';
import TimesheetCalendarView from '../va-timesheet/components/TimesheetCalendarView';
import EmployerHeaderNav from 'components/navigation/EmployerHeaderNav';
import { getVABySlug, parseVASlug } from '../../utils/urlHelpers';
import { exportTimesheetToExcel, validateExportData } from '../../services/exportService';
import TimesheetEntrySkeleton from '../../components/timesheet/TimesheetEntrySkeleton';
import TimesheetCalendarSkeleton from '../../components/timesheet/TimesheetCalendarSkeleton';

const TimeTrackerPage = () => {
  const { vaName } = useParams(); // Changed from vaId to vaName
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  const [timesheetData, setTimesheetData] = useState([]);
  const [contractData, setContractData] = useState(null);
  const [vaProfile, setVAProfile] = useState(null);
  const [selectedVA, setSelectedVA] = useState(null); // New state for resolved VA
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    // Default to showing this month
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [activeTab, setActiveTab] = useState('timesheet');
  const [isExporting, setIsExporting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const isEmployer = userProfile?.role === 'employer';
  const isVA = userProfile?.role === 'va';
  const vaId = selectedVA?.id; // Get vaId from resolved VA
  const canViewTimesheet = isEmployer || (isVA && userProfile?.id === vaId);

  // Handle window resize for responsive calendar view
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      // Auto-switch to list view on mobile
      if (!desktop && viewMode === 'calendar') {
        setViewMode('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  // Resolve VA from name slug - needed for both employers viewing VA timesheets and VAs viewing own
  const fetchVAProfile = useCallback(async () => {
    if (!vaName) return;

    try {
      // Get VA by name slug instead of ID
      const result = await getVABySlug(vaName);
      
      if (result.success) {
        setSelectedVA(result.data);
        setVAProfile(result.data);
      } else {
        setError(`VA not found: ${result.error}`);
        // Redirect to employer dashboard if VA not found
        navigate('/employer-dashboard');
      }
    } catch (error) {
      console.error('Error fetching VA profile:', error);
      setError('Failed to load VA profile');
    }
  }, [vaName, navigate]);

  // Fetch VA profile when vaName changes - for both employers and VAs
  useEffect(() => {
    if (vaName && userProfile?.id) {
      fetchVAProfile();
    }
  }, [vaName, userProfile?.id, fetchVAProfile]);

  // Get page title showing the VA's name
  const getPageTitle = () => {
    if (selectedVA) {
      return `Time Tracker - ${selectedVA.full_name}`;
    }
    if (vaName) {
      return `Time Tracker - ${parseVASlug(vaName)}`;
    }
    return 'Time Tracker';
  };

  // Clear error after a timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Main effect: Fetch all timesheet data when dependencies change
  useEffect(() => {
    if (!canViewTimesheet) {
      navigate('/unauthorized');
      return;
    }
    
    if (!vaId || !userProfile?.id) {
      console.log('Waiting for vaId and userProfile to load...');
      return;
    }

    const fetchAllData = async () => {
      console.log('Fetching timesheet data:', { 
        vaId, 
        employerId: userProfile.id, 
        isEmployer, 
        dateRange,
        statusFilter 
      });

      setIsLoading(true);
      setError(null);

      try {
        // Use the date range directly from state
        const startTime = new Date(dateRange.start + 'T00:00:00').toISOString();
        const endTime = new Date(dateRange.end + 'T23:59:59').toISOString();
        console.log('Query times - Start:', startTime, 'End:', endTime);
        
        // Build query for timesheet entries from the main table
        let query = supabase
          .from('timesheet_entries')
          .select(`
            *,
            project:projects (
              id,
              name
            ),
            employer:user_profiles!employer_id (
              id,
              full_name,
              company_name
            )
          `)
          .gte('start_time', startTime)
          .lte('start_time', endTime)
          .eq('va_id', vaId)
          .order('start_time', { ascending: false });

        // Filter by employer if user is employer
        if (isEmployer) {
          query = query.eq('employer_id', userProfile.id);
          console.log('Adding employer filter:', userProfile.id);
        }

        // Filter by status if not 'all'
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter);
          console.log('Adding status filter:', statusFilter);
        }

        const { data: entries, error: entriesError } = await query;
        if (entriesError) {
          console.error('Error fetching timesheet entries:', entriesError);
          throw entriesError;
        }

        console.log('Fetch successful, entries found:', entries?.length || 0);
        setTimesheetData(entries || []);

      } catch (err) {
        console.error('Timesheet data fetch error:', err);
        setError(err.message || 'Failed to load timesheet data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [canViewTimesheet, navigate, vaId, userProfile?.id, dateRange, statusFilter, isEmployer]);

  // Separate effect for fetching contract and profile data
  useEffect(() => {
    if (!userProfile?.id || !vaId || (!isEmployer && !isVA)) return;

    const fetchAdditionalData = async () => {
      try {
        // Fetch contract data
        const { data: contract, error: contractError } = await supabase
          .from('contracts')
          .select(`
            *,
            jobs(title, description),
            va:user_profiles!va_id(full_name, profile_picture, email),
            employer:user_profiles!employer_id(full_name, company_name)
          `)
          .eq('va_id', vaId)
          .eq('status', 'active')
          .maybeSingle();

        if (contractError && contractError.code !== 'PGRST116') {
          console.error('Contract fetch error:', contractError);
        } else if (contract) {
          console.log('[TimeTrackerPage] Contract loaded:', {
            contractId: contract.id,
            vaId: contract.va_id,
            employerId: contract.employer_id,
            currentUserId: userProfile.id,
            currentUserRole: userProfile.role,
            isEmployer
          });
          
          // Additional employer validation for security
          if (isEmployer && contract.employer_id !== userProfile.id) {
            console.warn('[TimeTrackerPage] Access denied: Employer ID mismatch', {
              contractEmployerId: contract.employer_id,
              currentUserId: userProfile.id
            });
            return;
          }
          setContractData(contract);
          console.log('[TimeTrackerPage] ContractData set successfully');
        } else {
          console.log('[TimeTrackerPage] No contract found for VA:', vaId);
        }

        // Fetch VA profile for display
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, full_name, profile_picture, email, hourly_rate')
          .eq('id', vaId)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
        } else if (profile) {
          setVAProfile(profile);
        }
      } catch (err) {
        console.error('Error fetching additional data:', err);
      }
    };

    fetchAdditionalData();
  }, [userProfile?.id, vaId, isEmployer, isVA]);

  // Callback version for real-time subscription and manual refresh
  const refetchTimesheetData = useCallback(async () => {
    if (!userProfile?.id || !vaId) {
      console.log('Skipping refetch: missing userProfile.id or vaId');
      return;
    }

    console.log('Refetching timesheet data for:', { 
      vaId, 
      employerId: userProfile.id, 
      isEmployer, 
      dateRange,
      statusFilter 
    });

    try {
      // Use the date range directly from state
      const startTime = new Date(dateRange.start + 'T00:00:00').toISOString();
      const endTime = new Date(dateRange.end + 'T23:59:59').toISOString();
      console.log('Refetch query times - Start:', startTime, 'End:', endTime);
      
      // Build query for timesheet entries from the main table
      let query = supabase
        .from('timesheet_entries')
        .select('*')
        .gte('start_time', startTime)
        .lte('start_time', endTime)
        .eq('va_id', vaId)
        .order('start_time', { ascending: false });

      // Filter by employer if user is employer
      if (isEmployer) {
        query = query.eq('employer_id', userProfile.id);
      }

      // Filter by status if not 'all'
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data: entries, error: entriesError } = await query;
      if (entriesError) {
        console.error('Error refetching timesheet entries:', entriesError);
        return;
      }

      console.log('Refetch successful, found entries:', entries?.length || 0);
      setTimesheetData(entries || []);

    } catch (err) {
      console.error('Timesheet refetch error:', err);
    }
  }, [userProfile?.id, vaId, dateRange, statusFilter, isEmployer]);

  // Set up real-time subscription for timesheet updates
  useEffect(() => {
    if (!vaId || !userProfile?.id) return;

    // For employers, we need to filter by employer_id OR va_id
    // For VAs, we filter by va_id only
    const channelName = isEmployer 
      ? `timesheet_changes_employer_${userProfile.id}_va_${vaId}`
      : `timesheet_changes_va_${vaId}`;

    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'timesheet_entries',
          // Filter based on user role - employers see their entries, VAs see their own
          filter: isEmployer 
            ? `employer_id=eq.${userProfile.id}`
            : `va_id=eq.${vaId}`
        },
        (payload) => {
          console.log('Real-time update received:', payload.eventType, payload);
          // Only refetch if this entry is relevant to current view
          if (isEmployer) {
            // For employers, check if the entry is for the current VA
            if (payload.new?.va_id === vaId || payload.old?.va_id === vaId) {
              refetchTimesheetData();
            }
          } else {
            // For VAs, refetch on any change
            refetchTimesheetData();
          }
        }
      )
      .subscribe((status) => {
        console.log('Timesheet subscription status:', status, 'for channel:', channelName);
      });

    return () => {
      console.log('Cleaning up timesheet subscription for channel:', channelName);
      supabase.removeChannel(subscription);
    };
  }, [vaId, userProfile?.id, isEmployer, refetchTimesheetData]);

  const handleApproveEntry = async (entryId) => {
    if (!isEmployer) {
      setError('Only employers can approve timesheet entries');
      return;
    }

    console.log('Approving entry:', entryId);

    try {
      const { error } = await supabase
        .from('timesheet_entries')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: userProfile.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', entryId);

      if (error) throw error;

      console.log('Entry approved successfully');

      // Update local state optimistically
      setTimesheetData(prev => prev.map(entry => 
        entry.id === entryId 
          ? { 
              ...entry, 
              status: 'approved', 
              approved_at: new Date().toISOString(),
              approved_by: userProfile.id,
              updated_at: new Date().toISOString() 
            }
          : entry
      ));

      // Refetch to ensure we have the latest data
      setTimeout(() => refetchTimesheetData(), 500);
      
    } catch (error) {
      console.error('Error approving entry:', error);
      setError(error.message || 'Failed to approve entry');
    }
  };

  const handleRejectEntry = async (entryId, reason) => {
    if (!isEmployer) {
      setError('Only employers can reject timesheet entries');
      return;
    }

    console.log('Rejecting entry:', entryId, 'reason:', reason);

    try {
      const { error } = await supabase
        .from('timesheet_entries')
        .update({
          status: 'rejected',
          rejection_reason: reason || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', entryId);

      if (error) throw error;

      console.log('Entry rejected successfully');

      // Update local state optimistically
      setTimesheetData(prev => prev.map(entry => 
        entry.id === entryId 
          ? { 
              ...entry, 
              status: 'rejected', 
              rejection_reason: reason || null,
              updated_at: new Date().toISOString() 
            }
          : entry
      ));

      // Refetch to ensure we have the latest data
      setTimeout(() => refetchTimesheetData(), 500);
      
    } catch (error) {
      console.error('Error rejecting entry:', error);
      setError(error.message || 'Failed to reject entry');
    }
  };

  const handleCreateEntry = async (entryData) => {
    if (!contractData?.id || !vaId) {
      setError('Unable to create entry: Missing contract or VA information');
      return;
    }

    // Only VAs or employers can create entries
    if (!isVA && !isEmployer) {
      setError('Unable to create entry: Insufficient permissions');
      return;
    }

    try {
      // Extract date from the start_time
      const dateStr = new Date(entryData.start_time).toISOString().split('T')[0];
      
      // Calculate total_amount
      const hourlyRate = contractData?.hourly_rate || vaProfile?.hourly_rate || 0;
      const totalAmount = (entryData.hours_worked || 0) * hourlyRate;

      const { error } = await supabase
        .from('timesheet_entries')
        .insert({
          contract_id: contractData.id,
          va_id: vaId,
          employer_id: userProfile.id,
          project_id: entryData.project_id || null,
          date: dateStr,
          start_time: entryData.start_time,
          end_time: entryData.end_time || null,
          description: entryData.description || '',
          task_completed: entryData.task_completed || null,
          is_billable: entryData.is_billable !== false,
          hours_worked: entryData.hours_worked || 0,
          hourly_rate: hourlyRate,
          total_amount: totalAmount,
          status: isEmployer ? 'approved' : 'pending'  // Employer entries are auto-approved, VA entries are pending
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh data to get the complete entry with calculated hours
      await refetchTimesheetData();
      setIsCreatingEntry(false);
      
    } catch (error) {
      console.error('Error creating timesheet entry:', error);
      setError(error.message || 'Failed to create entry');
    }
  };

  // Calculate summary stats
  const summary = timesheetData.reduce((acc, entry) => {
    const hours = entry.hours_worked || 0;
    const rate = entry.hourly_rate || contractData?.hourly_rate || vaProfile?.hourly_rate || 0;
    const amount = entry.total_amount || (hours * rate);

    acc.totalHours += hours;
    acc.totalAmount += amount;
    acc.approvedHours += entry.status === 'approved' ? hours : 0;
    acc.pendingHours += entry.status === 'pending' ? hours : 0;
    acc.rejectedHours += entry.status === 'rejected' ? hours : 0;
    
    return acc;
  }, {
    totalHours: 0,
    totalAmount: 0,
    approvedHours: 0,
    pendingHours: 0,
    rejectedHours: 0
  });

  // Export handler for Excel
  const handleExportToExcel = useCallback(async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Validate data
      const validation = validateExportData(timesheetData, vaProfile || selectedVA);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Prepare contracts array (employer view has single contract context)
      const contracts = contractData ? [contractData] : [];

      // Export to Excel
      await exportTimesheetToExcel({
        entries: timesheetData,
        summary: {
          totalHours: summary.totalHours,
          approvedHours: summary.approvedHours,
          pendingHours: summary.pendingHours,
          rejectedHours: summary.rejectedHours,
          totalAmount: summary.totalAmount,
          approvedAmount: timesheetData
            .filter(e => e.status === 'approved')
            .reduce((sum, e) => sum + (e.total_amount || 0), 0)
        },
        contracts,
        userProfile: vaProfile || selectedVA,
        dateRange,
        statusFilter,
        contractFilter: 'all'
      });

      console.log('[TimeTracker] Successfully exported to Excel');
    } catch (err) {
      console.error('[TimeTracker] Export failed:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [timesheetData, summary, contractData, vaProfile, selectedVA, dateRange, statusFilter]);

  // Handle VA not found case
  if (!selectedVA && vaName && !isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <EmployerHeaderNav />
        <div className="text-center">
          <Icon name="UserX" size={48} className="text-error mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-text-primary mb-2">VA Not Found</h1>
          <p className="text-text-secondary mb-4">
            No VA found with the name "{parseVASlug(vaName)}".
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/employer-dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!canViewTimesheet) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <EmployerHeaderNav />
        <div className="text-center">
          <Icon name="Lock" size={48} className="text-text-muted mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h2>
          <p className="text-text-secondary">You don't have permission to view this timesheet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <EmployerHeaderNav />
      
      {/* Error Banner */}
      {error && (
        <div className="bg-error-50 border-b border-error-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-error mr-2" />
              <p className="text-error-600 text-sm">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => setError(null)}
              className="text-error hover:text-error-700"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => {
                  console.log('Back button clicked');
                  // Navigate based on user role and context
                  if (isEmployer) {
                    // For employers, go back to hired VAs page
                    navigate('/employer-dashboard?tab=hired');
                  } else {
                    // For VAs, try to go back or fallback to dashboard
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate('/job-search');
                    }
                  }
                }}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {getPageTitle()}
                </h1>
                {/* Breadcrumb Navigation */}
                <nav className="text-sm text-gray-500 mb-2">
                  <Link to="/employer-dashboard" className="hover:text-blue-600">
                    Dashboard
                  </Link>
                  <span className="mx-2">›</span>
                  <span>Time Tracker</span>
                  {selectedVA && (
                    <>
                      <span className="mx-2">›</span>
                      <span className="text-gray-900">{selectedVA.full_name}</span>
                    </>
                  )}
                </nav>
                <p className="text-text-secondary">
                  {contractData?.jobs?.title || 'Project & Time Management'}
                </p>
              </div>
            </div>
            
            {isVA && activeTab === 'timesheet' && (
              <Button
                variant="primary"
                iconName="Plus"
                onClick={() => setIsCreatingEntry(true)}
              >
                Add Entry
              </Button>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('timesheet')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'timesheet'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon name="Clock" size={16} className="inline mr-2" />
                Timesheet
              </button>
              
              {/* {isEmployer && (
                <button
                  onClick={() => setActiveTab('projects')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'projects'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon name="Briefcase" size={16} className="inline mr-2" />
                  Projects
                </button>
              )} */}
            </nav>
          </div>

          {/* VA Info for Employers */}
          {isEmployer && (selectedVA || vaProfile) && (
            <div className="flex items-center space-x-3 p-4 bg-secondary-50 rounded-lg">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shadow-sm overflow-hidden">
                {(selectedVA || vaProfile)?.profile_picture ? (
                  <img
                    src={(selectedVA || vaProfile)?.profile_picture}
                    alt={(selectedVA || vaProfile)?.full_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center ${(selectedVA || vaProfile)?.profile_picture ? 'hidden' : ''}`}
                  style={{ display: (selectedVA || vaProfile)?.profile_picture ? 'none' : 'flex' }}
                >
                  {(selectedVA || vaProfile)?.full_name?.charAt(0).toUpperCase() || '?'}
                </div>
              </div>
              <div>
                <h3 className="font-medium text-text-primary">{(selectedVA || vaProfile)?.full_name}</h3>
                <p className="text-sm text-text-secondary">{(selectedVA || vaProfile)?.email}</p>
                <p className="text-sm text-text-secondary">
                  Rate: ${contractData?.hourly_rate || (selectedVA || vaProfile)?.hourly_rate || 0}/hr
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Tab Content */}
          {activeTab === 'timesheet' && (
            <>
              {/* Filters and Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <TimesheetFilters
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                    isEmployer={isEmployer}
                    onExportClick={handleExportToExcel}
                    isExporting={isExporting}
                    canExport={timesheetData.length > 0}
                  />
                </div>
                
                <div className="lg:col-span-2">
                  <div className="">
                  <TimesheetSummary
                    summary={summary}
                    hourlyRate={contractData?.hourly_rate || vaProfile?.hourly_rate || 0}
                    dateRange={dateRange}
                  />
                  </div>
                  <div className="my-4">
              {/* Project Manager */}
              {contractData && (
                <ProjectManager
                  contractData={contractData}
                  employerProfile={userProfile}
                  vaProfile={selectedVA || vaProfile}
                  isEmployer={isEmployer}
                  isVA={isVA}
                  onProjectsLoaded={setProjects}
                />
              )}
              </div>
                </div>
              </div>

              

              {/* Timesheet Entries */}
              <div className="bg-white rounded-lg border border-border">
                <div className="p-6 border-b border-border flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-text-primary">Time Entries</h2>
                  
                  {/* View Toggle - Desktop only */}
                  {isDesktop && (
                    <div className="flex items-center gap-2">
                      <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                        <button
                          onClick={() => setViewMode('list')}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            viewMode === 'list'
                              ? 'bg-white text-blue-600 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Icon name="List" size={16} className="inline mr-1.5" />
                          List
                        </button>
                        <button
                          onClick={() => setViewMode('calendar')}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            viewMode === 'calendar'
                              ? 'bg-white text-blue-600 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Icon name="Calendar" size={16} className="inline mr-1.5" />
                          Calendar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* List View */}
                {viewMode === 'list' && (
                  <div className="divide-y divide-border">
                    {isLoading ? (
                      <div className="space-y-3 p-4">
                        {[...Array(5)].map((_, index) => (
                          <TimesheetEntrySkeleton 
                            key={`skeleton-${index}`}
                            delay={index * 50}
                            viewMode="list"
                          />
                        ))}
                      </div>
                    ) : timesheetData.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="Clock" size={48} className="text-text-muted mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-text-primary mb-2">
                          {error ? 'Failed to Load Entries' : 'No Time Entries'}
                        </h3>
                        <p className="text-text-secondary mb-4">
                          {error 
                            ? 'There was a problem loading the timesheet entries.' 
                            : isVA 
                              ? "You haven't logged any time yet." 
                              : "No time entries found for the selected date range. Try adjusting your filters above."
                          }
                        </p>
                        <div className="space-x-2">
                          {error && (
                            <Button
                              variant="outline"
                              iconName="RefreshCw"
                              onClick={refetchTimesheetData}
                            >
                              Try Again
                            </Button>
                          )}
                          {isVA && !error && (
                            <Button
                              variant="primary"
                              iconName="Plus"
                              onClick={() => setIsCreatingEntry(true)}
                            >
                              Add First Entry
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      timesheetData.map((entry) => (
                        <TimesheetEntry
                          key={entry.id}
                          entry={entry}
                          isEmployer={isEmployer}
                          onApprove={handleApproveEntry}
                          onReject={handleRejectEntry}
                          onRefresh={refetchTimesheetData}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* Calendar View */}
                {viewMode === 'calendar' && isDesktop && (
                  <div className="p-6">
                    {isLoading ? (
                      <TimesheetCalendarSkeleton />
                    ) : timesheetData.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="Calendar" size={48} className="text-text-muted mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-text-primary mb-2">No Time Entries</h3>
                        <p className="text-text-secondary mb-4">
                          No time entries found for the selected date range.
                        </p>
                        {error && (
                          <Button
                            variant="outline"
                            iconName="RefreshCw"
                            onClick={refetchTimesheetData}
                          >
                            Try Again
                          </Button>
                        )}
                      </div>
                    ) : (
                      <TimesheetCalendarView
                        entries={timesheetData}
                        contracts={contractData ? [contractData] : []}
                        dateRange={dateRange}
                        onEntryClick={(entry) => console.log('Entry clicked:', entry)}
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Create Entry Modal */}
        {isCreatingEntry && (
          <TimesheetEntryForm
            isOpen={isCreatingEntry}
            onClose={() => setIsCreatingEntry(false)}
            onSubmit={handleCreateEntry}
            contractData={contractData}
            vaProfile={vaProfile}
            projects={projects}
          />
        )}
      </div>
    </div>
  );
};

export default TimeTrackerPage;
